import React, { useState } from 'react';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import I18n from '@/modules/i18n';
import { tv } from 'tailwind-variants';
import ButtonWithAdjacentDropdown from '@/components/shared/TextControlsToolbar/Button/ButtonWithAdjacentDropdown';
import useSpeechstream from '@/components/shared/TextToSpeechButton/useSpeechStream';
import DropdownButton from '@/components/ui/Dropdown/DropdownButton';
import Icon from '@/components/ui/Icon';
import Spinner from '@/components/common/Spinner';
import { TCITooltip } from '@/components/ui/Tooltip';

interface LanguageSelectMenuProps {
  locale: Locale;
  restoreSelection: () => void;
  selectedLanguage: string;
  setLanguage: (language: string) => void;
}

const containerVariants = tv({
  base: [
    'tw-max-h-96 tw-box-border',
    'tw-overflow-y-scroll',
    'tw-border tw-border-t-2 tw-border-b-0 tw-border-x-0 tw-border-solid tw-border-grey',
  ],
  variants: {
    isLoading: {
      true: 'tw-border-t-0 tw-w-64',
    },
  },
});

const LanguageOption = ({
  checkmarkPlacement = null,
  disabled,
  language,
  onClick,
  selected = false,
  translationLanguagesMap,
}: {
  checkmarkPlacement?: 'left' | 'right' | null;
  disabled?: boolean;
  language: string;
  onClick: () => void;
  selected?: boolean;
  translationLanguagesMap: () => Record<string, boolean>;
}) => (
  <ButtonWithAdjacentDropdown.Close asChild>
    <DropdownButton
      checkmarkPlacement={checkmarkPlacement}
      className="tw-gap-1"
      disabled={disabled}
      selected={selected}
      onClick={onClick}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
        {language}
        {translationLanguagesMap()[language] && (
          <TCITooltip side="right">
            <TCITooltip.Trigger asChild>
              <span className="tw-inline-flex">
                <Icon icon="Audio" size={16} />
              </span>
            </TCITooltip.Trigger>
            <TCITooltip.Portal>
              <TCITooltip.Content className="tw-z-9999">
                {I18n.t('this_language_supports_read_aloud')}
              </TCITooltip.Content>
            </TCITooltip.Portal>
          </TCITooltip>
        )}
      </div>
    </DropdownButton>
  </ButtonWithAdjacentDropdown.Close>
);

const LanguageSelectMenu = ({
  locale, selectedLanguage, restoreSelection, setLanguage,
}: LanguageSelectMenuProps) => {
  const { translationLanguagesMap } = useSpeechstream({ idToRead: '', locale });

  const translationLanguages = Object.keys(translationLanguagesMap());
  const isLoading = translationLanguages.length === 0;
  const [searchString, setSearchString] = useState(null);
  const filteredLanguages = Object.entries(translationLanguagesMap())
    .filter(([key]) => key.toLowerCase().startsWith(searchString?.toLowerCase()));
  const filteredLanguagesMap = Object.fromEntries(filteredLanguages);

  const handleLanguageChange = (language: string) => {
    restoreSelection();

    Axios.patch(Routes.plato_api_settings_path(), {
      settings: {
        text_to_speech_language: language,
      },
    }).then(() => {
      setLanguage(language);
    });
  };

  if (isLoading) {
    return (
      <div className={containerVariants({ isLoading })}>
        <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-xs">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div>
      <LanguageOption
        key={`language-${selectedLanguage}-selected`}
        selected
        checkmarkPlacement="right"
        language={selectedLanguage}
        translationLanguagesMap={translationLanguagesMap}
        onClick={() => handleLanguageChange(selectedLanguage)}
      />
      <hr className="tw-m-0" />
      <div className="tw-relative tw-px-4 tw-py-2 tw-bg-[#f5f5f5]">
        <Icon
          className="tw-absolute tw-left-6 tw-top-1/2 tw--translate-y-1/2"
          color="#777770"
          icon="Search"
          size={18}
        />
        <input
          className="tw-w-[195px] tw-rounded tw-border-solid tw-border-[#cccccc] tw-p-2 tw-pl-7"
          placeholder={I18n.t('search_languages')}
          type="text"
          value={searchString || ''}
          onChange={(e) => {
            setSearchString(e.target.value);
          }}
        />
      </div>
      <div
        className={containerVariants({ isLoading })}
      >
        {searchString && !filteredLanguages.length ? (
          <p className="tw-p-[1.5rem]">{I18n.t('no_languages_found')}</p>
        ) : (
          <>
            {Object.keys(searchString ? filteredLanguagesMap : translationLanguagesMap()).sort().map(language => (
              <LanguageOption
                key={`language-${language}`}
                disabled={selectedLanguage?.toLowerCase() === language?.toLowerCase()}
                language={language}
                translationLanguagesMap={translationLanguagesMap}
                onClick={() => handleLanguageChange(language)}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default LanguageSelectMenu;
